/** Typed same-origin API client. All paths proxied by vite dev / nginx. */

import type { RoundConfig } from '@hnplay/shared';
import type { ProductInfo } from './choice-overlay.js';

export interface ShellConfig {
  game: string;
  experimentId: string | null;
  enabledGames: string[];
  consent: { decided: boolean; analytics: boolean; marketing: boolean };
  player: {
    id: string;
    verified: boolean;
    displayName: string | null;
    declaredGoal: string | null;
    leaderboardConsent: boolean;
  };
}

export interface IssuedRun {
  runId: string;
  seed: string;
  sessionToken: string;
  config: RoundConfig;
  products: ProductInfo[];
}

export interface RunStatus {
  runId: string;
  status: 'ISSUED' | 'SUBMITTED' | 'VERIFIED' | 'INVALID';
  verifiedScore: number | null;
  invalidReason: string | null;
  rank: number | null;
}

export interface LeaderboardData {
  gameId: string;
  rows: Array<{ rank: number; handle: string; score: number; isMe: boolean }>;
  me: { rank: number; score: number } | null;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`api ${res.status}`);
  return res.json() as Promise<T>;
}

export const api = {
  config: () => fetch('/api/config').then((r) => json<ShellConfig>(r)),
  consent: (analytics: boolean, marketing: boolean) =>
    fetch('/api/consent', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ analytics, marketing }),
    }).then((r) => json<{ ok: true }>(r)),
  issueRun: (gameId: string, gameParams?: Record<string, number | string>) =>
    fetch('/api/runs', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(gameParams ? { gameId, gameParams } : { gameId }),
    }).then((r) => json<IssuedRun>(r)),
  submitRun: (sessionToken: string, encodedLog: string, clientScore: number) =>
    fetch('/api/runs/submit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionToken, encodedLog, clientScore }),
    }).then((r) => json<{ runId: string; status: string }>(r)),
  runStatus: (runId: string) => fetch(`/api/runs/${runId}`).then((r) => json<RunStatus>(r)),
  leaderboard: (gameId: string) => fetch(`/api/leaderboard?gameId=${gameId}`).then((r) => json<LeaderboardData>(r)),
  magicLink: (email: string, pendingRunId?: string) =>
    fetch('/api/auth/magic-link', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(pendingRunId ? { email, pendingRunId } : { email }),
    }),
  profile: (data: { displayName?: string; declaredGoal?: string; leaderboardConsent?: boolean }) =>
    fetch('/api/profile', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(data),
    }),
  storeClick: (gameId: string) =>
    fetch('/api/track/store-click', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ gameId }),
    }).catch(() => undefined),
  logout: () => fetch('/api/auth/logout', { method: 'POST' }),
  garden: () => fetch('/api/garden').then((r) => json<GardenData>(r)),
  plant: (botanical: string, slot: number) =>
    fetch('/api/garden/plant', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ botanical, slot }),
    }),
};

export interface GardenData {
  seeds: number;
  slots: Array<{ slot: number; botanical: string; stage: string; plantedAt: string } | null>;
}
